import React from 'react';
import TextInput from '../../components/TextInput';
import WheelDatePicker from '../../components/WheelDatePicker';
import ChoicePicker from '../../components/ChoicePicker';
import { sendIcon } from '../../chatIcons';
import { normalizeMyNumber } from '../../GeneralProfile/useGeneralProfileFlow';
import type { SpouseSectionFlow } from './useSpouseSectionFlow';
import { isSpouseChangeReasonCode, SPOUSE_CHANGE_REASON_CODES } from './spouseChangeReason';
import type { DisabilityCategory, NonResidentReason, SpouseChangeReasonCode, YesNo } from './spouseSectionTypes';

function limitMyNumberInput(value: string): string {
  return normalizeMyNumber(value).slice(0, 12);
}

function DatePickerOkFooter({ onSubmit, submitAriaLabel }: { onSubmit: () => void; submitAriaLabel: string }) {
  return (
    <div className="ff-text-input__footer">
      <button type="button" className="ff-text-input__send" onClick={onSubmit} aria-label={submitAriaLabel}>
        <img src={sendIcon} alt="" aria-hidden />
      </button>
    </div>
  );
}

function DatePickerStep({
  flow,
  step,
  wheelLocale,
  value,
  onChange,
  onSubmit,
}: {
  flow: SpouseSectionFlow;
  step: SpouseSectionFlow['step'];
  wheelLocale?: string;
  value: SpouseSectionFlow['dob'];
  onChange: SpouseSectionFlow['setDob'];
  onSubmit: () => void;
}) {
  return (
    <ComposerWithHelp flow={flow} step={step}>
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <WheelDatePicker value={value} onChange={onChange} locale={wheelLocale} />
        </div>
        <DatePickerOkFooter
          onSubmit={onSubmit}
          submitAriaLabel={flow.t('submitDocuments.profileName.send')}
        />
      </div>
    </ComposerWithHelp>
  );
}

const YES_NO = (t: SpouseSectionFlow['t']): { value: YesNo; label: string }[] => [
  { value: 'yes', label: t('submitDocuments.dependentDeduction.spouse.yes') },
  { value: 'no', label: t('submitDocuments.dependentDeduction.spouse.no') },
];

const DISABILITY_OPTIONS = (t: SpouseSectionFlow['t']): { value: DisabilityCategory; label: string }[] => [
  { value: 'none', label: t('submitDocuments.profileName.disabilityCategory.option.none') },
  { value: 'general', label: t('submitDocuments.profileName.disabilityCategory.option.general') },
  { value: 'special', label: t('submitDocuments.profileName.disabilityCategory.option.special') },
];

function ComposerWithHelp({
  flow,
  step,
  children,
}: {
  flow: SpouseSectionFlow;
  step: SpouseSectionFlow['step'];
  children: React.ReactNode;
}) {
  const help = flow.helpTextForStep(step);
  return (
    <>
      {help ? (
        <div
          style={{
            padding: '10px 16px 0',
            fontSize: '0.9em',
            lineHeight: 1.55,
            color: 'var(--ff-text-muted, #555)',
          }}
        >
          {help}
        </div>
      ) : null}
      {children}
    </>
  );
}

const CHANGE_REASON_OPTIONS = (t: SpouseSectionFlow['t']): { value: SpouseChangeReasonCode; label: string }[] =>
  SPOUSE_CHANGE_REASON_CODES.map((code) => ({
    value: code,
    label: t(`submitDocuments.dependentDeduction.spouse.changeReason.option.${code}`),
  }));

const NON_RESIDENT_OPTIONS = (t: SpouseSectionFlow['t']): { value: NonResidentReason; label: string }[] => [
  { value: 'study_abroad', label: t('submitDocuments.familyInfo.nonResident.study_abroad') },
  { value: 'disability', label: t('submitDocuments.familyInfo.nonResident.disability') },
  { value: 'remittance_380k', label: t('submitDocuments.familyInfo.nonResident.remittance_380k') },
];

export default function SpouseSectionChatComposer({
  flow,
  wheelLocale,
}: {
  flow: SpouseSectionFlow;
  wheelLocale?: string;
}) {
  const { step, t } = flow;

  if (step === 'notMarriedDone' || step === 'ineligibleNotice') return null;

  if (flow.confirmOnlyFromFamilyInfo && step !== 'employeeEstimatedIncome') return null;

  if (step === 'addSpouseName' || step === 'confirmFullName') {
    return (
      <ComposerWithHelp flow={flow} step={step}>
      <TextInput
        fields={[
          {
            id: 'name',
            label: t('submitDocuments.dependentDeduction.spouse.fullNameLabel'),
            required: true,
            validate: flow.validateLegalName,
          },
        ]}
        values={{ name: flow.spouse.fullName }}
        onChange={(_, v) => flow.setSpouse((s) => ({ ...s, fullName: v }))}
        onSubmit={() =>
          void (step === 'addSpouseName' ? flow.handleSendAddSpouseName() : flow.handleConfirmName())
        }
        submitDisabled={!flow.canSendAddSpouseName && !flow.canSendConfirmName}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
      </ComposerWithHelp>
    );
  }

  if (step === 'addSpouseKana' || step === 'confirmFullNameKana') {
    return (
      <ComposerWithHelp flow={flow} step={step}>
      <TextInput
        fields={[
          {
            id: 'kana',
            label: t('submitDocuments.dependentDeduction.spouse.fullNameKanaLabel'),
            required: true,
            validate: flow.validateKana,
          },
        ]}
        values={{ kana: flow.spouse.fullNameKana }}
        onChange={(_, v) => flow.setSpouse((s) => ({ ...s, fullNameKana: v }))}
        onSubmit={() =>
          void (step === 'addSpouseKana' ? flow.handleSendAddSpouseKana() : flow.handleConfirmKana())
        }
        submitDisabled={!flow.canSendAddSpouseKana && !flow.canSendConfirmKana}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
      </ComposerWithHelp>
    );
  }

  if (step === 'addSpouseDob') {
    return (
      <DatePickerStep
        flow={flow}
        step={step}
        wheelLocale={wheelLocale}
        value={flow.dob}
        onChange={flow.setDob}
        onSubmit={() => void flow.handleSendAddSpouseDob()}
      />
    );
  }

  if (step === 'confirmDob') {
    return (
      <DatePickerStep
        flow={flow}
        step={step}
        wheelLocale={wheelLocale}
        value={flow.dob}
        onChange={flow.setDob}
        onSubmit={() => void flow.handleConfirmDob()}
      />
    );
  }

  if (step === 'confirmMyNumber') {
    return (
      <ComposerWithHelp flow={flow} step={step}>
      <TextInput
        fields={[
          {
            id: 'mn',
            label: t('submitDocuments.profileName.field.myNumber'),
            type: 'tel',
            maxLength: 12,
            required: true,
            validate: flow.validateMyNumber,
          },
        ]}
        values={{ mn: flow.spouse.myNumber }}
        onChange={(_, v) => flow.setSpouse((s) => ({ ...s, myNumber: limitMyNumberInput(v) }))}
        onSubmit={() => void flow.handleConfirmMyNumber()}
        submitDisabled={!flow.canSendConfirmMyNumber}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
      </ComposerWithHelp>
    );
  }

  if (step === 'sharedLivelihood') {
    return (
      <ComposerWithHelp flow={flow} step={step}>
      <ChoicePicker
        value={flow.decl.sharedLivelihood || null}
        options={YES_NO(t)}
        onChange={(v) => void flow.handleSharedLivelihood(v)}
      />
      </ComposerWithHelp>
    );
  }

  if (step === 'employeeEstimatedIncome') {
    return (
      <ComposerWithHelp flow={flow} step={step}>
      <TextInput
        fields={[
          {
            id: 'income',
            label: t('submitDocuments.dependentDeduction.spouse.employeeIncomeLabel'),
            required: true,
          },
        ]}
        values={{ income: flow.decl.employeeEstimatedIncome }}
        onChange={(_, v) => flow.setDecl((d) => ({ ...d, employeeEstimatedIncome: v }))}
        onSubmit={() => void flow.handleEmployeeIncome()}
        submitDisabled={!flow.canSendEmployeeIncome}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
      </ComposerWithHelp>
    );
  }

  if (step === 'spouseFamilyWorker') {
    return (
      <ComposerWithHelp flow={flow} step={step}>
      <ChoicePicker
        value={flow.decl.spouseFamilyWorker || null}
        options={YES_NO(t)}
        onChange={(v) => void flow.handleFamilyWorker(v)}
      />
      </ComposerWithHelp>
    );
  }

  if (step === 'spouseEstimatedIncome') {
    return (
      <ComposerWithHelp flow={flow} step={step}>
      <TextInput
        fields={[
          {
            id: 'income',
            label: t('submitDocuments.dependentDeduction.spouse.spouseIncomeLabel'),
            required: true,
          },
        ]}
        values={{ income: flow.spouse.estimatedIncome }}
        onChange={(_, v) => flow.setSpouse((s) => ({ ...s, estimatedIncome: v }))}
        onSubmit={() => void flow.handleSpouseIncome()}
        submitDisabled={!flow.canSendSpouseIncome}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
      </ComposerWithHelp>
    );
  }

  if (step === 'sameHousehold') {
    return (
      <ChoicePicker
        value={flow.spouse.livingInSameHousehold || null}
        options={YES_NO(t)}
        onChange={(v) => void flow.handleSameHousehold(v)}
      />
    );
  }

  if (step === 'addressIfNot') {
    return (
      <TextInput
        fields={[
          {
            id: 'addr',
            label: t('submitDocuments.dependentDeduction.spouse.addressLabel'),
            required: true,
          },
        ]}
        values={{ addr: flow.spouse.ifNotAddress }}
        onChange={(_, v) => flow.setSpouse((s) => ({ ...s, ifNotAddress: v }))}
        onSubmit={() => void flow.handleAddressIfNot()}
        submitDisabled={!flow.canSendAddress}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'nonResident') {
    return (
      <ChoicePicker
        value={flow.spouse.nonResidentFlag || null}
        options={YES_NO(t)}
        onChange={(v) => void flow.handleNonResident(v)}
      />
    );
  }

  if (step === 'nonResidentReason') {
    return (
      <ChoicePicker
        value={flow.spouse.nonResidentReason || null}
        options={NON_RESIDENT_OPTIONS(t)}
        onChange={(v) => void flow.handleNonResidentReason(v)}
      />
    );
  }

  if (step === 'disabilityStatus') {
    return (
      <ChoicePicker
        value={flow.spouse.disabilityCategory || null}
        options={DISABILITY_OPTIONS(t)}
        onChange={(v) => void flow.handleDisability(v)}
      />
    );
  }

  if (step === 'changeDuringYear') {
    return (
      <ChoicePicker
        value={flow.spouse.changeDuringYear || null}
        options={YES_NO(t)}
        onChange={(v) => void flow.handleChangeDuringYear(v)}
      />
    );
  }

  if (step === 'changeDate') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <WheelDatePicker value={flow.changeDob} onChange={flow.setChangeDob} locale={wheelLocale} />
        </div>
        <DatePickerOkFooter
          onSubmit={() => void flow.handleChangeDate()}
          submitAriaLabel={t('submitDocuments.profileName.send')}
        />
      </div>
    );
  }

  if (step === 'changeReason') {
    const selected = isSpouseChangeReasonCode(flow.spouse.changeReason) ? flow.spouse.changeReason : null;
    return (
      <ChoicePicker
        value={selected}
        options={CHANGE_REASON_OPTIONS(t)}
        onChange={(v) => void flow.handleChangeReasonChoice(v)}
      />
    );
  }

  if (step === 'changeReasonOther') {
    return (
      <TextInput
        fields={[
          {
            id: 'reason',
            label: t('submitDocuments.dependentDeduction.spouse.changeReasonOtherLabel'),
            required: true,
          },
        ]}
        values={{ reason: flow.changeReasonOtherDraft }}
        onChange={(_, v) => flow.setChangeReasonOtherDraft(v)}
        onSubmit={() => void flow.handleChangeReasonOther()}
        submitDisabled={!flow.canSendChangeReasonOther}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  return null;
}
